/** Client portal: estimated tax payment schedules (confirm each payment) and sending a tax letter to the firm. */
import { formatCents, parseDollars } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, Mail } from "lucide-react";
import { useRef, useState } from "react";
import { Empty, Loading, PageHeader, Section } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { daysBetween, formatDate, localToday } from "@/lib/format";
import { cn } from "@/lib/utils";

type Payment = { id: string; installment: number; dueOn: string; amountCents: number; paidOn: string | null; paidCents: number | null; confirmation: string };
type Plan = { id: string; clientId: string; taxYear: number; jurisdiction: string; authority: { name: string; form: string; payUrl: string; payLabel: string }; datesEstimated?: boolean; payments: Payment[] };
const STATE_NAMES: Record<string, string> = { MD: "Maryland", VA: "Virginia" };
type PortalNotice = { id: string; clientId: string; authority: string; noticeCode: string; taxYear: number | null; status: "new" | "open" | "responded" | "resolved"; receivedOn: string | null; respondedOn: string | null; resolvedOn: string | null };

const NOTICE_STATUS: Record<PortalNotice["status"], string> = {
  new: "Received — we'll review it shortly",
  open: "We're working on it",
  responded: "We've responded; waiting for the agency",
  resolved: "Resolved",
};

function ConfirmPayment({ p, onDone }: { p: Payment; onDone: () => void }) {
  const [v, setV] = useState({ paidOn: localToday(), amount: (p.amountCents / 100).toFixed(2), confirmation: "" });
  const save = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/portal/estimates/payments/${p.id}/paid`, { paidOn: v.paidOn, paidCents: parseDollars(v.amount), confirmation: v.confirmation }), onSuccess: onDone });
  return (
    <form
      className="mt-3 grid gap-2 rounded-md bg-muted/40 p-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="space-y-1">
        <Label htmlFor={`pd-${p.id}`}>Paid on</Label>
        <Input id={`pd-${p.id}`} type="date" required value={v.paidOn} onChange={(e) => setV({ ...v, paidOn: e.target.value })} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`pa-${p.id}`}>Amount</Label>
        <Input id={`pa-${p.id}`} inputMode="decimal" required value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`pc-${p.id}`}>Confirmation number</Label>
        <Input id={`pc-${p.id}`} placeholder="Optional" value={v.confirmation} onChange={(e) => setV({ ...v, confirmation: e.target.value })} />
      </div>
      <Button type="submit" disabled={save.isPending}>
        Confirm
      </Button>
      {save.error && <p className="text-sm text-destructive sm:col-span-4">{errorMessage(save.error)}</p>}
    </form>
  );
}

function PlanCard({ plan, clientName }: { plan: Plan; clientName?: string }) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const today = localToday();
  const next = plan.payments.find((p) => !p.paidOn && p.amountCents > 0);
  const paid = plan.payments.reduce((a, p) => a + (p.paidCents ?? 0), 0);
  const total = plan.payments.reduce((a, p) => a + p.amountCents, 0);
  return (
    <Section
      title={`${plan.taxYear} ${plan.authority.name} estimated tax`}
      description={`${clientName ? `${clientName} · ` : ""}Form ${plan.authority.form} · ${formatCents(paid)} of ${formatCents(total)} paid`}
      action={
        <a href={plan.authority.payUrl} target="_blank" rel="noreferrer" className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary hover:underline">
          Pay online <ExternalLink className="h-3.5 w-3.5" />
        </a>
      }
    >
      {next && (
        <div className={cn("mb-4 rounded-lg border px-4 py-3", next.dueOn < today ? "border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40" : "border-primary/20 bg-primary/5")}>
          <p className="text-xs text-muted-foreground">Next payment</p>
          <p className="text-2xl font-semibold tabular">{formatCents(next.amountCents)}</p>
          <p className="text-sm">
            due {formatDate(next.dueOn)}
            {plan.datesEstimated && " (estimated date)"}
            {next.dueOn === today ? " · today" : next.dueOn > today ? ` · in ${daysBetween(today, next.dueOn)} day${daysBetween(today, next.dueOn) === 1 ? "" : "s"}` : " · past due"}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {plan.authority.payLabel}. Choose "estimated tax" ({plan.authority.form}) for {plan.taxYear}, then tell us below once it's paid.
          </p>
        </div>
      )}
      {plan.datesEstimated && (
        <p className="mb-2 text-xs text-muted-foreground">
          Due dates are estimates: a {STATE_NAMES[plan.jurisdiction] ?? "state"} holiday can move a payment to the next business day. We'll confirm each date before it's due.
        </p>
      )}
      <ul className="divide-y divide-border">
        {plan.payments.map((p) => (
          <li key={p.id} className="py-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="w-20 text-xs text-muted-foreground">Payment {p.installment}</span>
              <span className="w-28 text-sm tabular">
                {formatDate(p.dueOn)}
                {plan.datesEstimated && !p.paidOn && <span className="block text-xs text-muted-foreground">Estimated date</span>}
              </span>
              <span className="w-28 text-sm font-medium tabular">{formatCents(p.amountCents)}</span>
              {p.paidOn ? (
                <span className="flex items-center gap-1 text-sm text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-4 w-4" /> Paid {formatDate(p.paidOn)}
                  {p.confirmation && <span className="text-muted-foreground"> · #{p.confirmation}</span>}
                </span>
              ) : p.amountCents === 0 ? (
                <span className="text-sm text-muted-foreground">Nothing due</span>
              ) : (
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => setConfirming(confirming === p.id ? null : p.id)}>
                  {confirming === p.id ? "Cancel" : "I've paid this"}
                </Button>
              )}
            </div>
            {confirming === p.id && <ConfirmPayment p={p} onDone={() => setConfirming(null)} />}
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** The client uploads a letter from the IRS or state and sends it to the firm. */
function SendNotice({ clients }: { clients: Array<{ id: string; displayName: string }> }) {
  // The client list can arrive after this mounts (a page opened directly): default to the first.
  const [picked, setClientId] = useState("");
  const clientId = picked || clients[0]?.id || "";
  const [authority, setAuthority] = useState("federal");
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);
  const [noFile, setNoFile] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const send = useMutation({
    meta: { inlineError: true },
    mutationFn: async () => {
      const f = file.current!.files![0]!;
      const doc = await api.upload<{ id: string }>("/api/portal/documents", { clientId, folder: "Tax Notices" }, f);
      return api.send("POST", "/api/portal/notices", { clientId, documentId: doc.id, authority, note });
    },
    onSuccess: () => {
      setSent(true);
      setNote("");
      if (file.current) file.current.value = "";
    },
  });
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!file.current?.files?.[0]) {
          setNoFile(true);
          setSent(false);
          file.current?.focus();
          return;
        }
        send.mutate();
      }}
    >
      <p className="text-sm text-muted-foreground">Got a letter from the IRS or a state tax agency? Send it here; we'll review it and tell you what it means and what happens next. Don't pay or respond until we've looked at it, unless it says payment is due right away.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {clients.length > 1 && (
          <div className="space-y-1">
            <Label htmlFor="sn-client">For</Label>
            <select id="sn-client" className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="space-y-1">
          <Label htmlFor="sn-from">From</Label>
          <select id="sn-from" className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={authority} onChange={(e) => setAuthority(e.target.value)}>
            <option value="federal">IRS</option>
            <option value="MD">Maryland</option>
            <option value="VA">Virginia</option>
            <option value="DC">District of Columbia</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="sn-file">The letter</Label>
          <Input
            id="sn-file"
            ref={file}
            type="file"
            accept="application/pdf,image/*"
            aria-invalid={noFile}
            aria-describedby={noFile ? "sn-file-error" : undefined}
            onChange={() => setNoFile(false)}
          />
          {noFile && (
            <p id="sn-file-error" role="alert" className="text-sm text-destructive">
              Attach the letter (PDF or photo) before sending.
            </p>
          )}
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="sn-note">Anything we should know (optional)</Label>
        <Textarea id="sn-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      {send.error && <p className="text-sm text-destructive">{errorMessage(send.error)}</p>}
      {sent && <p className="text-sm text-emerald-700 dark:text-emerald-300">Sent. We'll be in touch.</p>}
      <Button type="submit" disabled={send.isPending}>
        <Mail className="h-4 w-4" /> Send to my accountant
      </Button>
    </form>
  );
}

export function PortalTaxPaymentsPage({ clients }: { clients: Array<{ id: string; displayName: string }> }) {
  const { data: plans, isLoading } = useQuery<Plan[]>({ queryKey: ["/api/portal/estimates"] });
  const { data: notices } = useQuery<PortalNotice[]>({ queryKey: ["/api/portal/notices"] });
  const name = (id: string) => (clients.length > 1 ? clients.find((c) => c.id === id)?.displayName : undefined);
  return (
    <>
      <PageHeader title="Tax Payments & Letters" description="Your estimated tax payment schedule, and letters from the IRS or state." />
      {isLoading ? (
        <Loading />
      ) : (
        <div className="space-y-6">
          {!plans?.length ? (
            <Empty>No estimated tax payments have been set up for you. If you have income without withholding, ask us whether you should pay estimated tax.</Empty>
          ) : (
            plans.map((p) => <PlanCard key={p.id} plan={p} clientName={name(p.clientId)} />)
          )}
          <Section title="Letters from the IRS or state">
            {!!notices?.length && (
              <ul className="mb-5 divide-y divide-border rounded-lg border border-border">
                {notices.map((n) => (
                  <li key={n.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm">
                    <span>
                      {n.authority === "federal" ? "IRS" : n.authority === "other" ? "Tax agency" : n.authority} {n.noticeCode || "letter"}
                      {n.taxYear && ` · ${n.taxYear}`}
                      <span className="block text-xs text-muted-foreground">Received {formatDate(n.receivedOn)}</span>
                    </span>
                    <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", n.status === "resolved" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200" : "bg-muted text-muted-foreground")}>{NOTICE_STATUS[n.status]}</span>
                  </li>
                ))}
              </ul>
            )}
            <SendNotice clients={clients} />
          </Section>
        </div>
      )}
    </>
  );
}
